import { Clock, CheckCircle, AlertCircle } from "@semo/icons";
import type { ProjectStatus, ApiStatus, StatusFilterOption } from "./types";
import { formatDateKorean } from "@semo/shared-utils";

/**
 * UI 상태별 스타일 설정
 * ✅ 2025-12-30: 3-state 모델로 단순화
 * - in-progress: 진행중
 * - on-hold: 대기 (시간상 마감)
 * - closed: 마감 (구매자 마감)
 */
export const UI_STATUS_CONFIG: Record<
  ProjectStatus,
  {
    label: string;
    color: string;
    textColor: string;
    bgColor: string;
    icon: typeof Clock;
    darkTextColor: string;
    darkBgColor: string;
    darkBorderColor: string;
  }
> = {
  "in-progress": {
    label: "진행중",
    color: "bg-primary",
    textColor: "text-primary",
    bgColor: "bg-primary/5",
    icon: Clock,
    darkTextColor: "text-primary",
    darkBgColor: "bg-primary/20",
    darkBorderColor: "border-primary/30",
  },
  "on-hold": {
    label: "대기",
    color: "bg-orange-500",
    textColor: "text-orange-600",
    bgColor: "bg-orange-50",
    icon: AlertCircle,
    darkTextColor: "text-orange-400",
    darkBgColor: "bg-orange-500/20",
    darkBorderColor: "border-orange-500/30",
  },
  closed: {
    label: "마감",
    color: "bg-green-500",
    textColor: "text-green-600",
    bgColor: "bg-green-50",
    icon: CheckCircle,
    darkTextColor: "text-green-400",
    darkBgColor: "bg-green-500/20",
    darkBorderColor: "border-green-500/30",
  },
};

/**
 * API의 견적 상태를 UI 상태로 변환
 * ✅ 2025-12-30: 3-state 모델로 단순화
 *
 * @param apiStatus - API에서 받은 상태값
 * @returns 정규화된 UI 상태
 *
 * @example
 * mapApiStatusToUiStatus('IN_PROGRESS') // → 'in-progress' (진행중)
 * mapApiStatusToUiStatus('ON_HOLD') // → 'on-hold' (대기)
 * mapApiStatusToUiStatus('CLOSED') // → 'closed' (마감)
 */
export function mapApiStatusToUiStatus(apiStatus?: string): ProjectStatus {
  switch (apiStatus) {
    case "IN_PROGRESS":
      return "in-progress"; // 진행중
    case "ON_HOLD":
      return "on-hold"; // 대기 (시간상 마감)
    case "CLOSED":
      return "closed"; // 마감 (구매자 마감)
    default:
      return "in-progress"; // 기본값: 진행중
  }
}

/**
 * 상태값이 유효한 ApiStatus인지 확인
 * ✅ 2025-12-30: 3-state 모델로 단순화
 *
 * @param status - 검증할 상태값
 * @returns 유효한 ApiStatus 여부
 */
export function isValidApiStatus(status: unknown): status is ApiStatus {
  const validStatuses: ApiStatus[] = ["IN_PROGRESS", "ON_HOLD", "CLOSED"];
  return typeof status === "string" && validStatuses.some((s) => s === status);
}

/**
 * 상태별 필터 옵션
 */
export const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { key: "IN_PROGRESS", label: "진행중" },
  { key: "ON_HOLD", label: "대기" },
  { key: "CLOSED", label: "마감" },
];

/**
 * API 상태별 스타일 설정 (공개 프로젝트 목록용)
 * frontend 앱의 디자인을 기준으로 함
 */
export const API_STATUS_CONFIG: Record<
  ApiStatus,
  { label: string; color: string }
> = {
  IN_PROGRESS: {
    label: "진행중",
    color: "text-primary-text bg-primary/5 border-primary/25",
  },
  ON_HOLD: {
    label: "대기",
    color: "text-orange-600 bg-orange-50 border-orange-200",
  },
  CLOSED: {
    label: "마감",
    color: "text-green-600 bg-green-50 border-green-200",
  },
};

/**
 * 날짜 문자열을 한국 형식으로 변환
 *
 * @param dateString - ISO 8601 형식의 날짜 문자열
 * @returns 포맷된 날짜 문자열 (예: 2025.11.27)
 */
export function formatDateForDisplay(dateString?: string): string {
  if (!dateString) return "-";
  return formatDateKorean(dateString);
}
